// Version check: detect newer deploy than this document or localStorage.

const VERSION_KEY = 'app_version';
const NOTIFICATION_SHOWN_KEY = 'version_notification_shown';

export const parseBuildId = (v) => {
  if (v == null || v === '' || v === 'dev' || v === 'unknown') return 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const versionJsonUrl = () => {
  const publicUrl = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
  return publicUrl ? `${publicUrl}/version.json` : '/version.json';
};

/** Build id claimed by this HTML document (inline script + meta). */
export const getPageBuildId = () => {
  if (typeof window === 'undefined' || typeof document === 'undefined') return 0;
  const fromWin = parseBuildId(window.__APP_BUILD_ID__);
  const meta = document.querySelector('meta[name="app-version"]');
  const fromMeta = parseBuildId(meta?.getAttribute('data-app-version'));
  return Math.max(fromWin, fromMeta);
};

/** Live server build from version.json (never trust cache). */
export const fetchServerBuildNumber = async () => {
  try {
    const bust = `${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
    const path = versionJsonUrl();
    const sep = path.includes('?') ? '&' : '?';
    const versionResponse = await fetch(`${path}${sep}t=${bust}&_=${bust}`, {
      cache: 'no-store',
      credentials: 'same-origin',
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        Pragma: 'no-cache',
        Expires: '0',
      },
    });
    if (!versionResponse.ok) return 0;
    const versionData = await versionResponse.json();
    const raw = versionData.buildNumber ?? versionData.timestamp ?? versionData.buildTime;
    return parseBuildId(raw);
  } catch (e) {
    return 0;
  }
};

/**
 * @deprecated use fetchServerBuildNumber for numeric compare
 */
export const getCurrentVersion = async () => {
  const n = await fetchServerBuildNumber();
  return n > 0 ? String(n) : 'unknown';
};

/**
 * True when the server has a newer build than this page or than we last recorded.
 */
export const checkForUpdates = async (onUpdateAvailable) => {
  try {
    const serverS = await fetchServerBuildNumber();
    if (serverS <= 0) {
      return false;
    }

    const pageB = getPageBuildId();
    const stoN = parseBuildId(localStorage.getItem(VERSION_KEY));
    const runningBuild = Math.max(pageB, stoN);

    if (serverS > runningBuild) {
      localStorage.setItem(VERSION_KEY, String(serverS));
      localStorage.setItem(NOTIFICATION_SHOWN_KEY, String(serverS));
      const hadPrior = stoN > 0 || pageB > 0;
      if (hadPrior && onUpdateAvailable) {
        onUpdateAvailable();
      }
      return hadPrior;
    }

    if (stoN <= 0 && serverS > 0) {
      localStorage.setItem(VERSION_KEY, String(serverS));
      localStorage.setItem(NOTIFICATION_SHOWN_KEY, String(serverS));
    }

    return false;
  } catch (error) {
    console.error('Error checking for updates:', error);
    return false;
  }
};

export const startVersionCheck = (onUpdateAvailable) => {
  if (!onUpdateAvailable || typeof onUpdateAvailable !== 'function') return;
  checkForUpdates(onUpdateAvailable);
};

export const stopVersionCheck = () => {};

export const reloadApp = () => {
  if ('caches' in window) {
    caches.keys().then((names) => {
      names.forEach((name) => {
        caches.delete(name);
      });
    });
  }

  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.getRegistrations().then((registrations) => {
      registrations.forEach((registration) => {
        registration.unregister();
      });
    });
  }

  if ('indexedDB' in window && indexedDB.databases) {
    indexedDB.databases().then((databases) => {
      databases.forEach((db) => {
        if (db.name) {
          indexedDB.deleteDatabase(db.name);
        }
      });
    });
  }

  const notificationVersion = localStorage.getItem(NOTIFICATION_SHOWN_KEY);
  sessionStorage.clear();
  if (notificationVersion) {
    localStorage.setItem(NOTIFICATION_SHOWN_KEY, notificationVersion);
  }

  const timestamp = Date.now();
  const url = new URL(window.location.href);
  url.searchParams.delete('_v');
  url.searchParams.delete('_cb');
  url.searchParams.delete('v');
  url.searchParams.delete('__v');
  url.searchParams.set('_v', timestamp);
  url.searchParams.set('_cb', timestamp);

  if (window.location.replace) {
    window.location.replace(url.toString());
  } else {
    window.location.href = url.toString();
  }

  setTimeout(() => {
    if (window.location.reload) {
      window.location.reload(true);
    }
  }, 50);
};
